import {
  AddressInput,
  Icons,
  Input,
  Label,
  MultiSelect,
  PhoneInput,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
  Textarea,
  Tooltip,
} from '@commenda/kakashi';
import { SecureInformationRequestFieldType } from '@prisma/client';
import { FieldProps, GroupProps } from './types';

interface SecureInformationRequestPreviewProps {
  groups: GroupProps[];
}

const SecureInformationRequestPreview = ({
  groups,
}: SecureInformationRequestPreviewProps) => {
  const renderField = (field: FieldProps, fieldIndex: number) => {
    switch (field.type) {
      case SecureInformationRequestFieldType.TEXT:
      case SecureInformationRequestFieldType.NUMBER:
        return (
          <div key={fieldIndex} className="space-y-xs">
            <div className="flex items-center gap-xs">
              <Label htmlFor={`preview-${fieldIndex}`}>
                {field.headerText}
              </Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="size-4 text-fg-tertiary" />
                </Tooltip>
              ) : null}
            </div>
            <Input
              id={`preview-${fieldIndex}`}
              readOnly
              type={
                field.type === SecureInformationRequestFieldType.NUMBER
                  ? 'number'
                  : 'text'
              }
              placeholder="Type here"
            />
          </div>
        );

      case SecureInformationRequestFieldType.BIG_TEXT:
        return (
          <div key={fieldIndex}>
            <div className="flex items-center gap-xs">
              <Label htmlFor={`preview-${fieldIndex}`}>
                {field.headerText}
              </Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <Textarea
              id={`preview-${fieldIndex}`}
              placeholder={`Enter ${field.headerText.toLowerCase()}`}
              className="mt-1"
              readOnly
            />
          </div>
        );

      case SecureInformationRequestFieldType.PHONE_NUMBER:
        return (
          <div key={fieldIndex}>
            <div className="flex items-center gap-xs">
              <Label htmlFor={`preview-${fieldIndex}`}>
                {field.headerText}
              </Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <PhoneInput
              id={`preview-${fieldIndex}`}
              placeholder="Enter phone number"
              disabled
            />
          </div>
        );

      case SecureInformationRequestFieldType.DATE:
        return (
          <div key={fieldIndex}>
            <div className="flex items-center gap-xs">
              <Label htmlFor={`preview-${fieldIndex}`}>
                {field.headerText}
              </Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <Input id={`preview-${fieldIndex}`} type="date" className="mt-1" />
          </div>
        );

      case SecureInformationRequestFieldType.SELECT:
        return (
          <div key={fieldIndex}>
            <div className="flex items-center gap-xs">
              <Label htmlFor={`preview-${fieldIndex}`}>
                {field.headerText}
              </Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <Select>
              <SelectTrigger disabled>
                <SelectValue placeholder="Select one" />
              </SelectTrigger>
            </Select>
          </div>
        );

      case SecureInformationRequestFieldType.MULTI_SELECT:
        return (
          <div key={fieldIndex}>
            <div className="flex items-center gap-xs">
              <Label htmlFor={`preview-${fieldIndex}`}>
                {field.headerText}
              </Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <MultiSelect
              defaultValue={[]}
              options={
                field?.selectOptions?.map((option) => ({
                  value: option,
                  label: option,
                })) || []
              }
              value={[]}
              disabled
              onValueChange={() => {}}
            />
          </div>
        );

      case SecureInformationRequestFieldType.ADDRESS:
        return (
          <div key={fieldIndex} className="space-y-3">
            <div className="flex items-center gap-xs">
              <Label>{field.headerText}</Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>

            <AddressInput
              address={{
                addressLine1: '',
                postalCode: '',
                city: '',
                state: '',
                country: '',
              }}
              onChange={() => {}}
              readonly
            />
          </div>
        );

      case SecureInformationRequestFieldType.FILE:
      case SecureInformationRequestFieldType.MULTI_FILE:
        return (
          <div key={fieldIndex} className="space-y-3">
            <div className="flex items-center gap-xs">
              <Label>{field.headerText}</Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <SingleFileUploader
              companyId={0}
              file={null}
              onFileChange={() => {}}
              setFile={() => {}}
              disabled
            />
          </div>
        );

      case SecureInformationRequestFieldType.RADIO:
        return (
          <div key={fieldIndex}>
            <div className="flex items-center gap-xs">
              <Label>{field.headerText}</Label>
              {field.tooltip ? (
                <Tooltip content={field.tooltip}>
                  <Icons.InfoCircle className="w-4 h-4 text-gray-500" />
                </Tooltip>
              ) : null}
            </div>
            <div className="flex gap-4 mt-2">
              <RadioGroup className="flex items-center gap-2">
                <div className="flex items-center gap-md">
                  <RadioGroupItem value="yes" disabled />
                  <Label htmlFor="yes">Yes</Label>
                </div>
                <div className="flex items-center gap-md">
                  <RadioGroupItem value="no" disabled />
                  <Label htmlFor="no">No</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-2xl">
      <h2 className="txt-medium-medium text-text-info">PREVIEW</h2>

      <div className="space-y-6">
        {groups.map((group, groupIndex) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`preview-group-${groupIndex}-${group.headerText}`}
            className="rounded-xl border border-border-primary bg-bg-primary p-2xl"
          >
            <div className="flex flex-col gap-3xl">
              <h3 className="txt-medium-bold">
                {group.headerText || `Group No. ${groupIndex + 1}`}
              </h3>

              <div className="space-y-4">
                {group.fields.map((field, fieldIndex) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={`preview-field-${groupIndex}-${fieldIndex}-${field.headerText}`}
                  >
                    {renderField(field, fieldIndex)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {groups.length === 0 && (
          <div className="p-6 border border-gray-200 rounded-lg bg-white">
            <div className="text-center text-gray-500">
              <p>No groups added yet. Start building your form on the left.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { SecureInformationRequestPreview };
